/**
 * Order History Sheet Component
 * Displays past and active restaurant food orders for the guest.
 */

import { getCurrentLanguage, t } from '../../../i18n';

export interface RestaurantOrderRecord {
  id: string;
  orderNumber: string;
  location?: string;
  roomNumber?: string;
  tableNumber?: string;
  orderMode: 'room' | 'dine';
  payMode: 'room' | 'tbl';
  outletId?: string;
  outletName?: any;
  items: Array<{
    id: string;
    name: any;
    price: number;
    qty: number;
  }>;
  subtotal: number;
  serviceCharge: number;
  total: number;
  status: 'received' | 'preparing' | 'on_the_way' | 'delivered' | 'cancelled' | string;
  createdAt: string;
}

export function renderOrderHistorySheet(
  orders: RestaurantOrderRecord[],
  isLoading: boolean = false
): string {
  const lang = getCurrentLanguage();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
      case 'received':
        return {
          bg: '#fef3c7',
          color: '#b45309',
          label: `🟡 ${t('statusOrderReceived')}`,
        };
      case 'preparing':
      case 'cooking':
        return {
          bg: '#e0f2fe',
          color: '#0369a1',
          label: `👨‍🍳 ${t('statusOrderPreparing')}`,
        };
      case 'ready':
        return {
          bg: '#fef08a',
          color: '#854d0e',
          label: `🛎️ ${t('statusOrderReady')}`,
        };
      case 'on_the_way':
        return {
          bg: '#f3e8ff',
          color: '#7e22ce',
          label: `🛵 ${t('statusOrderOnTheWay')}`,
        };
      case 'delivered':
      case 'served':
      case 'completed':
        return {
          bg: '#dcfce7',
          color: '#15803d',
          label: `🟢 ${t('statusOrderDelivered')}`,
        };
      case 'cancelled':
        return {
          bg: '#fee2e2',
          color: '#b91c1c',
          label: `❌ ${t('statusCancelled')}`,
        };
      default:
        return {
          bg: '#f1f5f9',
          color: '#475569',
          label: status,
        };
    }
  };

  let contentHtml = '';

  if (isLoading) {
    contentHtml = `
      <div style="padding:48px 16px;text-align:center;color:#64748b;">
        <div style="display:inline-block;width:32px;height:32px;border:3px solid #e2e8f0;border-top-color:#00B900;border-radius:50%;animation:spin 1s linear infinite;"></div>
        <p style="margin-top:12px;font-size:14px;">${t('loading')}</p>
      </div>
    `;
  } else if (!orders || orders.length === 0) {
    contentHtml = `
      <div style="padding:48px 16px;text-align:center;color:#64748b;">
        <div style="font-size:44px;margin-bottom:12px;">🍽️</div>
        <p style="font-size:15px;font-weight:600;color:#334155;margin-bottom:6px;">${t('noOrderHistory')}</p>
        <p style="font-size:13px;color:#94a3b8;margin-bottom:20px;">${t('menuEmptySubtitle')}</p>
        <button onclick="(window.closeOrderHistorySheet || window.closeOrderHistory)?.()" style="
          padding:10px 24px;background:#00B900;color:#fff;border:none;border-radius:10px;
          font-size:14px;font-weight:700;cursor:pointer;">
          ${t('orderMoreBtn')}
        </button>
      </div>
    `;
  } else {
    const listHtml = orders.map((ord) => {
      const badge = getStatusBadge(ord.status);
      const timeStr = ord.createdAt
        ? new Date(ord.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
      const dateStr = ord.createdAt
        ? new Date(ord.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })
        : '';

      const itemsHtml = (ord.items || []).map((it) => {
        const itemName = typeof it.name === 'object' && it.name
          ? it.name[lang] || it.name.en || it.name.th || 'Menu Item'
          : String(it.name || 'Menu Item');
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#334155;padding:3px 0;">
            <div style="flex:1;padding-right:8px;">${itemName} <span style="font-weight:700;color:#00B900;">×${it.qty}</span></div>
            <div style="font-weight:600;color:#1e293b;">฿${((it.price || 0) * (it.qty || 1)).toLocaleString()}</div>
          </div>
        `;
      }).join('');

      const outletText = (() => {
        const n = ord.outletName;
        if (!n) return '';
        if (typeof n === 'string') return n.trim();
        return n[lang] || n.en || n.th || '';
      })();

      const locNum = ord.orderMode === 'room'
        ? (ord.roomNumber || (ord.location || '').replace(/^(ห้อง|Room|房间|Номер|部屋|객실)\s*/i, '').trim())
        : (ord.tableNumber || (ord.location || '').replace(/^(โต๊ะ|Table|餐桌|Столик|テーブル|테이블)\s*/i, '').trim());

      const locText = ord.orderMode === 'room'
        ? `🛏️ ${t('roomBadge')} ${locNum}`
        : `🍽️ ${t('tableBadge')} ${locNum}`;

      return `
        <div style="background:#fff;border:1px solid #e2e8f0;border-radius:14px;padding:14px 16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,0.03);">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
            <div>
              <div style="font-size:14px;font-weight:800;color:#0f172a;letter-spacing:0.3px;">${ord.orderNumber}</div>
              <div style="font-size:11px;color:#94a3b8;margin-top:2px;">🕒 ${dateStr} ${timeStr}</div>
            </div>
            <span style="font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:${badge.bg};color:${badge.color};">
              ${badge.label}
            </span>
          </div>

          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
            ${outletText ? `
              <span style="font-size:11px;padding:2px 8px;border-radius:6px;background:#f0fdf4;color:#166534;border:1px solid #bbf7d0;font-weight:700;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-flex;align-items:center;gap:3px;" title="${outletText}">
                🍽️ ${outletText}
              </span>
            ` : ''}
            <span style="font-size:11px;padding:2px 8px;border-radius:6px;background:#f8fafc;color:#475569;border:1px solid #e2e8f0;font-weight:600;">
              ${locText}
            </span>
            <span style="font-size:11px;padding:2px 8px;border-radius:6px;background:#f8fafc;color:#475569;border:1px solid #e2e8f0;font-weight:600;">
              ${(ord.payMode === 'tbl' || (ord as any).paymentMode === 'tbl') ? t('payAtTable') : t('chargeToRoom')}
            </span>
          </div>

          <div style="border-top:1px dashed #e2e8f0;padding-top:8px;margin-bottom:8px;">
            ${itemsHtml}
          </div>

          <div style="border-top:1px solid #f1f5f9;padding-top:8px;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-size:12px;color:#64748b;font-weight:600;">${t('orderTotal')}</span>
            <span style="font-size:15px;font-weight:800;color:#00B900;">฿${(ord.total || 0).toLocaleString()}</span>
          </div>
        </div>
      `;
    }).join('');

    contentHtml = `
      <div style="padding:0 16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
          <span style="font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;">
            ${t('orderHistoryTitle')} (${orders.length})
          </span>
          <button onclick="window.refreshOrderHistory?.()" style="
            display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f1f5f9;border:none;border-radius:6px;
            font-size:11px;font-weight:700;color:#475569;cursor:pointer;">
            🔄 ${t('refreshBtn')}
          </button>
        </div>
        ${listHtml}
      </div>
    `;
  }

  return `
    <div id="order-history-overlay" onclick="(window.closeOrderHistorySheet || window.closeOrderHistory)?.()" style="
      position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:10001;
      display:flex;align-items:flex-end;
    ">
      <div onclick="event.stopPropagation()" style="
        width:100%;max-height:85vh;background:#f8fafc;
        border-radius:20px 20px 0 0;
        padding-bottom:env(safe-area-inset-bottom,0px);
        overflow-y:auto;-webkit-overflow-scrolling:touch;
      ">
        <div style="position:sticky;top:0;background:#fff;z-index:10;display:flex;align-items:center;justify-content:space-between;padding:16px;border-bottom:1px solid #e2e8f0;">
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:17px;font-weight:800;color:#0f172a;">${t('orderHistoryTitle')}</span>
          </div>
          <button onclick="(window.closeOrderHistorySheet || window.closeOrderHistory)?.()" style="
            width:32px;height:32px;border:none;background:#f1f5f9;border-radius:50%;
            font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;">✕</button>
        </div>
        <div style="padding-top:14px;">
          ${contentHtml}
        </div>
        <div style="height:24px;"></div>
      </div>
    </div>
  `;
}
